/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Footnote from '@utils/db/models/Footnote';
import VerseText from '@utils/db/models/VerseText';
import db from '@utils/db';
import {
  fetchVersesBatched,
} from '@utils/store/actions';
import useTypedDispatch from './useTypedDispatch';
import useTypedSelector from './useTypedSelector';

export default function useGetVersesForPassages(passages, skipFetch = false) {
  const dispatch = useTypedDispatch();
  const verses: any = useTypedSelector((state) => state.verses);
  const books = useTypedSelector((state) => state.books);

  const [_allPassageVerses, setAllPassageVerses] = useState({}); // @TODO: instead of useState, should this load verses from current translation into redux or other local state to avoid skeleton on every first render?

  const passagesKey = useMemo(() => JSON.stringify(passages), [passages]);

  // Track in-flight batched requests to avoid duplicate dispatches from the same component
  const inFlightRequestKeysRef = useRef<Set<string>>(new Set());
  // Track the last dispatched request per passagesKey so rerenders with identical needs don't re-dispatch
  const lastDispatchedKeyByPassagesRef = useRef<Map<string, string>>(new Map()); // @TODO: is this necessary? i don't totally understand this logic

  useEffect(() => {
    const fetchVerses = async () => { // @TODO: this is called too many times...not sure why
      const missingReferences: any[] = [];
      // @TODO: const newPassageVerses = {..._allPassageVerses}; // @TODO: need to spread _allPassageVerses? (recommended by chatgpt)
      const newPassageVerses = {};

      const chaptersToFetch = new Map();

      for (const p of passages) {
        const translationId = p.translation_id || p.translationId;
        const bookId = p.book_id || p.bookId;
        const chapterNum = p.chapter_number || p.chapterNumber;
        const startVerseNumber = p.start_verse_number || p.startVerseNumber;
        const endVerseNumber = p.end_verse_number || p.endVerseNumber;

        if (!translationId || !bookId || !chapterNum || !startVerseNumber || !endVerseNumber) {
          continue;
        }

        if (!newPassageVerses[p.id]) {
          chaptersToFetch.set(p.id, {translationId, bookId, chapterNum});
        }
      }

      const allVerseTexts = [];

      await Promise.all(Array.from(chaptersToFetch.entries()).map(async ([passageId, info]) => {
        const verseTexts = await VerseText.getAllByTranslationIdBookIdChapterNumber(
          db,
          info.translationId,
          info.bookId,
          info.chapterNum,
        );

        newPassageVerses[passageId] = {};
        verseTexts.forEach((v) => {
          newPassageVerses[passageId][v.verseNumber] = v;
          allVerseTexts.push(v);
        });
      }));

      const footnotes = await Footnote.getAllByVerseTextIds(db, allVerseTexts.map((verseText) => verseText.verseTextId));
      const footnotesByVerseTextId = {};
      footnotes.forEach((footnote) => {
        if (!footnotesByVerseTextId[footnote.verseTextId]) {
          footnotesByVerseTextId[footnote.verseTextId] = [];
        }
        footnotesByVerseTextId[footnote.verseTextId].push(footnote);
      });

      for (const p of passages) {
        const versesForChapterByVerseNumber = {};

        const translationId = p.translation_id || p.translationId;
        const bookId = p.book_id || p.bookId;
        const chapterNum = p.chapter_number || p.chapterNumber;
        const startVerseNumber = p.start_verse_number || p.startVerseNumber;
        const endVerseNumber = p.end_verse_number || p.endVerseNumber;

        if (translationId && bookId && chapterNum && startVerseNumber && endVerseNumber) {
          // it shouldn't be often that any of these are null/undefined, but when they are, don't do a fetch
          Object.values(newPassageVerses[p.id]).forEach((verseText: any) => {
            versesForChapterByVerseNumber[verseText.verseNumber] = {
              verseId: verseText.verseId,
              verseApiId: verseText.verseApiId,
              bookId: verseText.bookId,
              chapterNumber: verseText.chapterNumber,
              verseNumber: verseText.verseNumber,
              verseTextId: verseText.verseTextId,
              verseText: verseText.verseText,
              heading: verseText.heading,
              translationId: verseText.translationId,
              rangeStartVerseNumber: verseText.rangeStartVerseNumber,
              rangeEndVerseNumber: verseText.rangeEndVerseNumber,
              footnotes: footnotesByVerseTextId[verseText.verseTextId] || [],
            };
          });
          const missingVerseNumbers = [];
          for (let verseNum = startVerseNumber; verseNum <= endVerseNumber; verseNum++) {
            let verse = versesForChapterByVerseNumber[verseNum];
            if (verse === undefined || verse === null) { // @TODO: is this sufficient? (watch out for empty verse text)
              missingVerseNumbers.push(verseNum);
            }
            if (verse?.rangeStartVerseNumber && verse?.rangeEndVerseNumber) {
              for (let i = verse.rangeStartVerseNumber; i <= verse.rangeEndVerseNumber; i++) {
                // if verse in range, get those verses as well
                verse = versesForChapterByVerseNumber[verseNum];
                if ((verse === undefined || verse === null) && !missingVerseNumbers.includes(i)) { // @TODO: is this sufficient? (watch out for empty verse text)
                  missingVerseNumbers.push(verseNum); // @TODO: should this be i instead of verseNum?
                }
              }
            }
          }

          if (missingVerseNumbers.length) {
            missingReferences.push([translationId, bookId, chapterNum, missingVerseNumbers]);
          }
        }
        setAllPassageVerses((curr) => {
          return {
            ...curr,
            [p.id]: versesForChapterByVerseNumber,
          };
        });
      }

      if (missingReferences.length && !skipFetch) {
        const byChapterMap = new Map<string, {translationId: any, bookId: any, chapterNumber: any, verseNumbers: Set<number>}>();
        for (const [translationId, bookId, chapterNumber, verseNumbers] of missingReferences) {
          const key = `${translationId}_${bookId}_${chapterNumber}`;
          if (!byChapterMap.has(key)) {
            byChapterMap.set(key, {translationId, bookId, chapterNumber, verseNumbers: new Set<number>()});
          }
          const entry = byChapterMap.get(key);
          for (const vn of verseNumbers as number[]) {
            entry.verseNumbers.add(vn);
          }
        }
        const mergedRefs = Array.from(byChapterMap.values()).map(({translationId, bookId, chapterNumber, verseNumbers}) => [translationId, bookId, chapterNumber, Array.from(verseNumbers).sort((a, b) => a - b)]);

        const requestKey = JSON.stringify(mergedRefs);
        const lastKey = lastDispatchedKeyByPassagesRef.current.get(passagesKey);

        const cleanup = () => {
          inFlightRequestKeysRef.current.delete(requestKey);
        };

        if (mergedRefs.length > 0 && requestKey !== lastKey && !inFlightRequestKeysRef.current.has(requestKey)) {
          inFlightRequestKeysRef.current.add(requestKey);
          lastDispatchedKeyByPassagesRef.current.set(passagesKey, requestKey);
          Promise.resolve(dispatch(fetchVersesBatched(mergedRefs))).then(cleanup, cleanup);
        }
      }
    };

    fetchVerses();
  }, [passagesKey, skipFetch, dispatch, verses]); /* eslint-disable-line react-hooks/exhaustive-deps */ // @TODO: if `passages` is in the dep array, it's different on every re-render // @TODO: dep array should include translation.updated_at timestamp (do at the verse level?) // @TODO: verses needs to be here to reset state variable when the dispatch has completed

  return useMemo(() => {
    const versesByPassageId = {};
    return passages.reduce((acc, p) => {
      const translationId = p.translation_id || p.translationId; // @TODO: can't read `translation_id` of `undefined`
      const bookId = p.book_id || p.bookId;
      const book = books.find((b) => b.id === bookId);
      const chapterNum = p.chapter_number || p.chapterNumber;
      const startVerseNumber = p.start_verse_number || p.startVerseNumber;
      const endVerseNumber = p.end_verse_number || p.endVerseNumber;

      const retVerses = [];
      for (let verseNum = startVerseNumber; verseNum <= endVerseNumber; verseNum++) {
        const loadingVerse = {
          isLoading: true,
          verseNum, // @TODO: does this need to be here?
          verseApiId: `${book?.api_id}.${chapterNum}.${verseNum}`,
          translationId,
        }; // @TODO: maybe create a Verse class?
        const verseInDb = _allPassageVerses[p.id]?.[verseNum]; // @TODO: rename
        retVerses.push(!verseInDb ? loadingVerse : verseInDb);

        if (verseInDb?.rangeStartVerseNumber && verseInDb?.rangeEndVerseNumber) {
          for (let i = verseInDb.rangeStartVerseNumber; i <= verseInDb.rangeEndVerseNumber; i++) {
            // if verse in range, get those verses as well
            const loadingVerseRange = {
              isLoading: true,
              verseNum: i, // @TODO: does this need to be here?
              verseApiId: `${book?.api_id}.${chapterNum}.${i}`,
              translationId,
            }; // @TODO: maybe create a Verse class?
            const verseInDbRange = _allPassageVerses[p.id]?.[i];
            if (!retVerses.find((v) => v.verseApiId === verseInDbRange?.verseApiId)) {
              retVerses.push(!verseInDbRange ? loadingVerseRange : verseInDbRange);
            }
          }
        }
      }
      versesByPassageId[p.id] = retVerses;

      return {...acc, [p.id]: retVerses};
    }, {});
  }, [passagesKey, books, verses, _allPassageVerses]); /* eslint-disable-line react-hooks/exhaustive-deps */ // if `passages` is in the dep array, it's different on every re-render
}
